import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PencilIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { effectiveCapitalApi } from '../../helpers/effectiveCapitalApi';
import {
  readEffectiveCapitalCache,
  writeEffectiveCapitalCache,
} from '../../helpers/effectiveCapitalCache';
import {
  deltaClass,
  formatCurrency,
  formatSignedCurrency,
  formatDateLong,
  formatDateShort,
  formatDelta,
  formatSyncTimestamp,
  formatTimestamp,
  todayISO,
  yesterdayISO,
} from '../../helpers/effectiveCapitalFormat';
import { LoaderSpinner } from '../LoaderSpinner';
import { DatePickerInput } from '../DatePickerInput';

const EC_GREEN = '#e8f5e9';
const EC_GREEN_HEADER = '#c8e6c9';
const OVERRIDE_BG = '#E8F0FE';
const FLEX_BORDER = '3px solid #c62828';

function toNum(value) {
  const n = parseFloat(String(value ?? 0));
  return Number.isNaN(n) ? 0 : n;
}

function CurrencyCell({ value, isNegative }) {
  const cls = isNegative ? 'value-negative' : '';
  return (
    <td className={`px-3 py-2 text-right tabular-nums text-sm ${cls}`}>
      {isNegative || value < 0 ? `(${formatCurrency(Math.abs(value))})` : formatCurrency(value)}
    </td>
  );
}

function DeltaCell({ delta }) {
  if (delta === undefined) {
    return <td className="px-3 py-2 text-right tabular-nums text-sm text-gray-500">—</td>;
  }
  return (
    <td className={`px-3 py-2 text-right tabular-nums text-sm ${deltaClass(delta)}`}>
      {formatDelta(delta)}
    </td>
  );
}

function formatPaymentDue(card) {
  if (card.paymentDueDate) return card.paymentDueDate;
  if (card.nextPaymentDueDate) {
    return new Date(`${card.nextPaymentDueDate}T12:00:00`).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }
  return '—';
}

function postedBalance(card) {
  const limit = card.creditLimit ?? card.balanceLimit ?? 0;
  const hasAvail = card.availableBalance != null && card.availableBalance > 0;
  if (limit > 0 && hasAvail) return limit - card.availableBalance;
  return card.currentBalance;
}

function ConsolidatedTable({ summary, dodDeltas, date }) {
  // getDoDelta returns entity-level EC deltas: [{ entity: 'kings_loot' | 'kings_holding' | 'consolidated', delta, ... }]
  const entityDodMap = useMemo(() => {
    const m = new Map();
    (dodDeltas ?? []).forEach((d) => m.set(d.entity, d.delta));
    return m;
  }, [dodDeltas]);

  const hasDod = (dodDeltas ?? []).length > 0;
  const klDod = hasDod ? entityDodMap.get('kings_loot') : undefined;
  const khDod = hasDod ? entityDodMap.get('kings_holding') : undefined;
  const consolidatedDod = hasDod ? entityDodMap.get('consolidated') : undefined;

  return (
    <div className="mb-6">
      <div className="entity-header text-center py-2 text-sm font-bold">Effective Capital Summary</div>
      <div className="entity-header text-center py-1.5 text-xs">Consolidated Daily Brand Summary — {date}</div>
      <div className="overflow-x-auto">
        <table className="eadin-table">
          <thead>
            <tr>
              <th className="w-40">Brand</th>
              <th className="!text-right">Cash</th>
              <th className="!text-right">Receivables</th>
              <th className="!text-right">CC Liabilities</th>
              <th className="!text-right">OC Liabilities</th>
              <th className="!text-right" style={{ background: EC_GREEN_HEADER }}>Effective Capital</th>
              {hasDod && <th className="!text-right">EC Delta DoD</th>}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-3 py-2 text-sm font-medium">Kings Loot</td>
              <td className="px-3 py-2 text-right tabular-nums text-sm">
                $ {formatCurrency(summary?.kingsLoot?.cash ?? 0)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-sm">
                $ {formatCurrency(summary?.kingsLoot?.receivables ?? 0)}
              </td>
              <CurrencyCell value={summary?.kingsLoot?.ccLiabilities ?? 0} isNegative={(summary?.kingsLoot?.ccLiabilities ?? 0) < 0} />
              <CurrencyCell value={summary?.kingsLoot?.ocLiabilities ?? 0} isNegative={(summary?.kingsLoot?.ocLiabilities ?? 0) < 0} />
              <td className={`px-3 py-2 text-right tabular-nums text-sm font-semibold ${(summary?.kingsLoot?.effectiveCapital ?? 0) < 0 ? 'value-negative' : ''}`} style={{ background: EC_GREEN }}>
                $ {formatSignedCurrency(summary?.kingsLoot?.effectiveCapital ?? 0)}
              </td>
              {hasDod && <DeltaCell delta={klDod} />}
            </tr>
            <tr>
              <td className="px-3 py-2 text-sm font-medium">Kings Holding</td>
              <td className="px-3 py-2 text-right tabular-nums text-sm">
                $ {formatCurrency(summary?.kingsHolding?.cash ?? 0)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-sm">
                $ {formatCurrency(summary?.kingsHolding?.receivables ?? 0)}
              </td>
              <CurrencyCell value={summary?.kingsHolding?.ccLiabilities ?? 0} isNegative={(summary?.kingsHolding?.ccLiabilities ?? 0) < 0} />
              <CurrencyCell value={summary?.kingsHolding?.ocLiabilities ?? 0} isNegative={(summary?.kingsHolding?.ocLiabilities ?? 0) < 0} />
              <td className={`px-3 py-2 text-right tabular-nums text-sm font-semibold ${(summary?.kingsHolding?.effectiveCapital ?? 0) < 0 ? 'value-negative' : ''}`} style={{ background: EC_GREEN }}>
                $ {formatSignedCurrency(summary?.kingsHolding?.effectiveCapital ?? 0)}
              </td>
              {hasDod && <DeltaCell delta={khDod} />}
            </tr>
            <tr className="total-row">
              <td className="px-3 py-2 text-sm font-bold">Consolidated</td>
              <td className="px-3 py-2 text-right tabular-nums text-sm font-bold">
                $ {formatCurrency(summary?.consolidated?.cash ?? 0)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-sm font-bold">
                $ {formatCurrency(summary?.consolidated?.receivables ?? 0)}
              </td>
              <td className={`px-3 py-2 text-right tabular-nums text-sm font-bold ${(summary?.consolidated?.ccLiabilities ?? 0) < 0 ? 'value-negative' : ''}`}>
                $ ({formatCurrency(Math.abs(summary?.consolidated?.ccLiabilities ?? 0))})
              </td>
              <td className={`px-3 py-2 text-right tabular-nums text-sm font-bold ${(summary?.consolidated?.ocLiabilities ?? 0) < 0 ? 'value-negative' : ''}`}>
                $ ({formatCurrency(Math.abs(summary?.consolidated?.ocLiabilities ?? 0))})
              </td>
              <td className={`px-3 py-2 text-right tabular-nums text-sm font-bold ${(summary?.consolidated?.effectiveCapital ?? 0) < 0 ? 'value-negative' : ''}`} style={{ background: EC_GREEN }}>
                $ {formatSignedCurrency(summary?.consolidated?.effectiveCapital ?? 0)}
              </td>
              {hasDod && <DeltaCell delta={consolidatedDod} />}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BalanceOverrideInput({ value, onSave, onCancel }) {
  const [draft, setDraft] = useState(String(value));

  const commit = () => {
    const val = parseFloat(draft.replace(/[$,]/g, ''));
    if (!Number.isNaN(val)) onSave(val);
    else if (draft.trim() === '') onSave(null);
    else onCancel();
  };

  return (
    <input
      autoFocus
      type="text"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        }
        if (e.key === 'Escape') onCancel();
      }}
      className="w-28 border border-gray-300 px-1.5 py-0.5 text-right text-xs tabular-nums focus:outline-none focus:ring-1 focus:ring-gray-900"
      style={{ background: OVERRIDE_BG }}
    />
  );
}

function EditableBalanceCell({
  plaidAccountId,
  displayBalance,
  hasOverride,
  prefix = '$ ',
  wrapNegative = false,
  editingId,
  setEditingId,
  onSave,
}) {
  const isEditing = editingId === plaidAccountId;
  const formatted = wrapNegative
    ? `(${formatCurrency(displayBalance)})`
    : `${prefix}${formatCurrency(displayBalance)}`;

  if (!onSave) {
    return <span>{formatted}</span>;
  }

  return (
    <span className="inline-flex items-center gap-1.5 justify-end">
      {isEditing ? (
        <BalanceOverrideInput
          value={displayBalance}
          onSave={(val) => {
            onSave(plaidAccountId, val);
            setEditingId(null);
          }}
          onCancel={() => setEditingId(null)}
        />
      ) : (
        <>
          <span style={hasOverride ? { background: OVERRIDE_BG, padding: '1px 4px' } : {}}>{formatted}</span>
          <button
            type="button"
            onClick={() => setEditingId(plaidAccountId)}
            className="inline-flex items-center text-gray-500 hover:text-gray-900 transition-colors"
            title={hasOverride ? 'Edit balance override' : 'Override balance'}
          >
            <PencilIcon className="w-3 h-3 shrink-0" />
          </button>
          {hasOverride && (
            <button
              type="button"
              onClick={() => onSave(plaidAccountId, null)}
              className="inline-flex items-center text-gray-500 hover:text-gray-900 transition-colors"
              title="Clear override"
            >
              <XMarkIcon className="w-3 h-3 shrink-0" />
            </button>
          )}
        </>
      )}
    </span>
  );
}

// ─── Manual receivable row (non-Shopify/Amazon sources) ───────────────────────

function ManualReceivableEditRow({ row, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(row.amount));
  const ts = row.updatedAt ?? row.capturedAt ?? null;

  const handleBlur = () => {
    const val = parseFloat(draft.replace(/,/g, ''));
    if (!Number.isNaN(val) && val !== row.amount) onSave(row.source, row.label, val);
    setEditing(false);
  };

  return (
    <tr>
      <td className="px-3 py-2 text-sm">{row.label}</td>
      <td className="px-3 py-2 text-sm"><span className="text-amber-600 font-medium">Manual override</span></td>
      <td className="px-3 py-2 text-right text-sm text-gray-500 tabular-nums">
        {ts ? new Date(ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '—'}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-sm">
        {editing ? (
          <input
            autoFocus
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleBlur();
              if (e.key === 'Escape') { setDraft(String(row.amount)); setEditing(false); }
            }}
            className="w-32 border border-gray-300 px-2 py-0.5 text-right text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-gray-900"
            style={{ background: OVERRIDE_BG }}
          />
        ) : (
          <button
            type="button"
            onClick={() => { setDraft(String(row.amount)); setEditing(true); }}
            className="w-full text-right tabular-nums text-sm hover:underline"
            style={{ background: OVERRIDE_BG, padding: '2px 8px' }}
            title="Click to edit"
          >
            $ {formatCurrency(row.amount)}
          </button>
        )}
      </td>
    </tr>
  );
}

// ─── Shopify receivable row (live balance + expandable breakdown) ─────────────

function ShopifyReceivableRow({ row, liveBalance, breakdown, totalOverride, totalOverrideUpdatedAt, onSaveTotalOverride }) {
  const [expanded, setExpanded] = useState(false);
  const [confirmedExpanded, setConfirmedExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const hasBreakdown = !!breakdown;
  const hasOverride = totalOverride != null;
  const displayAmount = hasOverride ? totalOverride : liveBalance;
  const overrideTime = totalOverrideUpdatedAt
    ? new Date(totalOverrideUpdatedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : null;

  const commit = () => {
    const val = parseFloat(draft.replace(/[$,]/g, ''));
    if (!Number.isNaN(val)) onSaveTotalOverride?.(val);
    else if (draft.trim() === '') onSaveTotalOverride?.(null);
    setEditing(false);
  };

  return (
    <>
      <tr>
        <td className="px-3 py-2 text-sm">
          <button
            type="button"
            className="flex items-center gap-1 hover:text-gray-900 text-left w-full"
            onClick={() => hasBreakdown && setExpanded((v) => !v)}
            style={{ cursor: hasBreakdown ? 'pointer' : 'default' }}
          >
            {hasBreakdown ? (
              expanded ? <ChevronDownIcon className="w-3.5 h-3.5 shrink-0 text-gray-500" /> : <ChevronRightIcon className="w-3.5 h-3.5 shrink-0 text-gray-500" />
            ) : null}
            <span>{row.label}</span>
          </button>
        </td>
        <td className="px-3 py-2 text-sm text-gray-500">
          {hasOverride ? <span className="text-amber-600 font-medium">Manual override</span> : 'Shopify API'}
        </td>
        <td className="px-3 py-2 text-right text-sm text-gray-500 tabular-nums">
          {hasOverride ? (overrideTime ? `Set ${overrideTime}` : 'Today') : 'Live'}
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-sm">
          <span className="inline-flex items-center gap-1.5 justify-end">
            {editing ? (
              <input
                autoFocus
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); commit(); }
                  if (e.key === 'Escape') setEditing(false);
                }}
                className="w-28 border border-gray-300 px-1.5 py-0.5 text-right text-xs tabular-nums focus:outline-none focus:ring-1 focus:ring-gray-900"
                style={{ background: OVERRIDE_BG }}
              />
            ) : (
              <>
                <span style={hasOverride ? { background: OVERRIDE_BG, padding: '1px 4px' } : {}}>$ {formatCurrency(displayAmount)}</span>
                {onSaveTotalOverride && (
                  <button
                    type="button"
                    onClick={() => { setDraft(String(displayAmount)); setEditing(true); }}
                    className="inline-flex items-center text-gray-500 hover:text-gray-900 transition-colors"
                    title={hasOverride ? 'Edit total override' : 'Override total'}
                  >
                    <PencilIcon className="w-3 h-3 shrink-0" />
                  </button>
                )}
                {hasOverride && onSaveTotalOverride && (
                  <button
                    type="button"
                    onClick={() => onSaveTotalOverride(null)}
                    className="inline-flex items-center text-gray-500 hover:text-gray-900 transition-colors"
                    title="Clear total override"
                  >
                    <XMarkIcon className="w-3 h-3 shrink-0" />
                  </button>
                )}
              </>
            )}
          </span>
        </td>
      </tr>
      {expanded && breakdown && (
        <tr>
          <td colSpan={4} className="px-0 py-0">
            <div className="ml-8 mr-3 my-1.5 border border-gray-200 text-xs">
              <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100">
                <span className="text-gray-500">
                  Payout Balance <span className="text-[10px] text-gray-400">(unscheduled)</span>
                </span>
                <span className="tabular-nums text-gray-900">+${formatCurrency(breakdown.payoutBalance ?? 0)}</span>
              </div>
              {(breakdown.scheduledPayouts ?? []).map((p) => (
                <div key={`shopify-sched-${p.id}`} className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100">
                  <span className="text-gray-500">Scheduled <span className="text-gray-400">· {formatDateShort(p.date)}</span></span>
                  <span className="tabular-nums text-gray-900">+${formatCurrency(p.amount)}</span>
                </div>
              ))}
              {breakdown.scheduledTotal > 0 && (breakdown.scheduledPayouts ?? []).length > 1 && (
                <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100 bg-gray-50">
                  <span className="font-medium text-gray-900">Scheduled Subtotal</span>
                  <span className="tabular-nums font-medium text-gray-900">+${formatCurrency(breakdown.scheduledTotal)}</span>
                </div>
              )}
              {(breakdown.unconfirmedPayouts ?? []).map((p) => (
                <div key={`shopify-unconf-${p.id}`} className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100">
                  <span className="text-gray-500">Deposited, Not in Plaid <span className="text-gray-400">· {formatDateShort(p.date)}</span></span>
                  <span className="tabular-nums text-gray-900">+${formatCurrency(p.amount)}</span>
                </div>
              ))}
              {breakdown.unconfirmedDepositedTotal > 0 && (breakdown.unconfirmedPayouts ?? []).length > 1 && (
                <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100 bg-gray-50">
                  <span className="font-medium text-gray-900">Deposited Subtotal</span>
                  <span className="tabular-nums font-medium text-gray-900">+${formatCurrency(breakdown.unconfirmedDepositedTotal)}</span>
                </div>
              )}
              {(breakdown.confirmedPayouts ?? []).length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => setConfirmedExpanded((v) => !v)}
                    className="flex items-center justify-between w-full px-3 py-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <span className="flex items-center gap-1 line-through">
                      {confirmedExpanded ? <ChevronDownIcon className="w-3 h-3 shrink-0" /> : <ChevronRightIcon className="w-3 h-3 shrink-0" />}
                      Confirmed in Bank ({breakdown.confirmedPayouts.length})
                    </span>
                    <span className="tabular-nums line-through">${formatCurrency(breakdown.confirmedPayouts.reduce((s, p) => s + p.amount, 0))}</span>
                  </button>
                  {confirmedExpanded && breakdown.confirmedPayouts.map((p) => (
                    <div key={`shopify-conf-${p.id}`} className="flex items-center justify-between px-5 py-1 border-t border-gray-100 text-gray-300">
                      <span className="line-through">{formatDateShort(p.date)} <span className="text-[10px]">{p.status}</span></span>
                      <span className="tabular-nums line-through">${formatCurrency(p.amount)}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Amazon receivable row (live balance + expandable breakdown) ──────────────

function AmazonReceivableRow({
  row,
  liveBalance,
  breakdown,
  totalOverride,
  totalOverrideUpdatedAt,
  onSaveTotalOverride,
}) {
  const [expanded, setExpanded] = useState(false);
  const [confirmedExpanded, setConfirmedExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const hasBreakdown = !!breakdown;
  const hasOverride = totalOverride != null;
  const displayAmount = hasOverride ? totalOverride : liveBalance;
  const overrideTime = totalOverrideUpdatedAt
    ? new Date(totalOverrideUpdatedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : null;

  const commit = () => {
    const val = parseFloat(draft.replace(/[$,]/g, ''));
    if (!Number.isNaN(val)) onSaveTotalOverride?.(val);
    else if (draft.trim() === '') onSaveTotalOverride?.(null);
    setEditing(false);
  };

  return (
    <>
      <tr>
        <td className="px-3 py-2 text-sm">
          <button
            type="button"
            className="flex items-center gap-1 hover:text-gray-900 text-left w-full"
            onClick={() => hasBreakdown && setExpanded((v) => !v)}
            style={{ cursor: hasBreakdown ? 'pointer' : 'default' }}
          >
            {hasBreakdown ? (
              expanded ? <ChevronDownIcon className="w-3.5 h-3.5 shrink-0 text-gray-500" /> : <ChevronRightIcon className="w-3.5 h-3.5 shrink-0 text-gray-500" />
            ) : null}
            <span>{row.label}</span>
          </button>
        </td>
        <td className="px-3 py-2 text-sm text-gray-500">
          {hasOverride ? <span className="text-amber-600 font-medium">Manual override</span> : 'Amazon SP-API'}
        </td>
        <td className="px-3 py-2 text-right text-sm text-gray-500 tabular-nums">
          {hasOverride ? (overrideTime ? `Set ${overrideTime}` : 'Today') : 'Live'}
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-sm">
          <span className="inline-flex items-center gap-1.5 justify-end">
            {editing ? (
              <input
                autoFocus
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); commit(); }
                  if (e.key === 'Escape') setEditing(false);
                }}
                className="w-28 border border-gray-300 px-1.5 py-0.5 text-right text-xs tabular-nums focus:outline-none focus:ring-1 focus:ring-gray-900"
                style={{ background: OVERRIDE_BG }}
              />
            ) : (
              <>
                <span style={hasOverride ? { background: OVERRIDE_BG, padding: '1px 4px' } : {}}>$ {formatCurrency(displayAmount)}</span>
                {onSaveTotalOverride && (
                  <button
                    type="button"
                    onClick={() => { setDraft(String(displayAmount)); setEditing(true); }}
                    className="inline-flex items-center text-gray-500 hover:text-gray-900 transition-colors"
                    title={hasOverride ? 'Edit total override' : 'Override total'}
                  >
                    <PencilIcon className="w-3 h-3 shrink-0" />
                  </button>
                )}
                {hasOverride && onSaveTotalOverride && (
                  <button
                    type="button"
                    onClick={() => onSaveTotalOverride(null)}
                    className="inline-flex items-center text-gray-500 hover:text-gray-900 transition-colors"
                    title="Clear total override"
                  >
                    <XMarkIcon className="w-3 h-3 shrink-0" />
                  </button>
                )}
              </>
            )}
          </span>
        </td>
      </tr>
      {expanded && breakdown && (
        <tr>
          <td colSpan={4} className="px-0 py-0">
            <div className="ml-8 mr-3 my-1.5 border border-gray-200 text-xs">
              <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100">
                <span className="text-gray-500">
                  Total Balance
                  {breakdown.balanceSource === 'listBalances_total'
                    ? <span className="ml-1.5 text-[10px] text-gray-400">(includes Deferred Transactions)</span>
                    : <span className="ml-1.5 text-[10px] text-amber-600">(open groups fallback)</span>}
                </span>
                <span className="tabular-nums text-gray-900">+${formatCurrency(breakdown.totalBalance ?? breakdown.amazonTotalBalance ?? 0)}</span>
              </div>
              {(breakdown.unconfirmedGroups ?? []).map((p) => (
                <div key={`amz-unconf-${p.id}`} className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100">
                  <span className="text-gray-500">
                    Disbursed, Not in Plaid
                    <span className="text-gray-400"> · {formatDateShort(p.date)}</span>
                    {p.fundTransferStatus && <span className="text-gray-400"> · {p.fundTransferStatus}</span>}
                  </span>
                  <span className="tabular-nums text-gray-900">+${formatCurrency(p.amount)}</span>
                </div>
              ))}
              {breakdown.unconfirmedDisbursedTotal > 0 && (breakdown.unconfirmedGroups ?? []).length > 1 && (
                <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100 bg-gray-50">
                  <span className="font-medium text-gray-900">Disbursed Subtotal</span>
                  <span className="tabular-nums font-medium text-gray-900">+${formatCurrency(breakdown.unconfirmedDisbursedTotal)}</span>
                </div>
              )}
              {(breakdown.confirmedGroups ?? []).length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => setConfirmedExpanded((v) => !v)}
                    className="flex items-center justify-between w-full px-3 py-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <span className="flex items-center gap-1 line-through">
                      {confirmedExpanded ? <ChevronDownIcon className="w-3 h-3 shrink-0" /> : <ChevronRightIcon className="w-3 h-3 shrink-0" />}
                      Confirmed in Bank ({breakdown.confirmedGroups.length})
                    </span>
                    <span className="tabular-nums line-through"><span>$</span>${formatCurrency(breakdown.confirmedGroups.reduce((s, p) => s + p.amount, 0))}</span>
                  </button>
                  {confirmedExpanded && breakdown.confirmedGroups.map((p) => (
                    <div key={`amz-conf-${p.id}`} className="flex items-center justify-between px-5 py-1 border-t border-gray-100 text-gray-300">
                      <span className="line-through">{formatDateShort(p.date)} <span className="text-[10px]">{p.status}</span></span>
                      <span className="tabular-nums line-through"><span>$</span>${formatCurrency(p.amount)}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function OperatingCreditRow({ facility, onSave }) {
  const [editingField, setEditingField] = useState(null);
  const [draft, setDraft] = useState('');

  const commit = (field) => {
    const parsed = parseFloat(draft.replace(/[$,]/g, ''));
    if (!isNaN(parsed) && parsed !== facility[field]) {
      onSave?.(facility.id, field, parsed);
    }
    setEditingField(null);
  };

  const startEdit = (field) => {
    setDraft(String(facility[field] ?? 0));
    setEditingField(field);
  };

  const amountDueSettlement = facility.settlement;
  const appliedPayments = amountDueSettlement?.payments ?? [];
  const pendingPayments = amountDueSettlement?.pendingPayments ?? [];
  const latestAppliedPayment = appliedPayments.reduce((latest, payment) => (
    !latest || String(payment.date).localeCompare(String(latest.date)) > 0 ? payment : latest
  ), null);
  const appliedPaymentTitle = appliedPayments.map((payment) => (
    `${payment.name} $${formatCurrency(payment.appliedAmount ?? payment.amount)} ${payment.date}`
      + (payment.confirmationNumber ? ` · Bill.com #${payment.confirmationNumber}` : '')
  )).join('\n');
  const amountDueFootnote = (
    amountDueSettlement?.bills?.length
      || amountDueSettlement?.paymentsApplied > 0
      || pendingPayments.length > 0
  ) ? (
    <div className="mt-0.5 flex flex-col items-end text-[10px]">
      {amountDueSettlement?.bills?.length
        ? (() => {
          const openBills = amountDueSettlement.bills.filter((bill) => bill.amountDue > 0);
          const nextDue = openBills
            .map((bill) => bill.dueDate)
            .filter(Boolean)
            .sort()[0];
          return (
            <div className="text-gray-400" title={appliedPaymentTitle}>
              {openBills.length > 0
                ? `Bill.com invoice due - ${nextDue || '—'}`
                : `Bill.com invoice paid - ${latestAppliedPayment?.date || '—'}`}
            </div>
          );
        })()
        : amountDueSettlement?.paymentsApplied > 0 && (
        <div
          className="text-gray-400"
          title={appliedPaymentTitle}
        >
          Paid ${formatCurrency(amountDueSettlement.paymentsApplied)} · {latestAppliedPayment?.date}
        </div>
        )}
      {pendingPayments.length > 0 && (
        <div className="text-amber-600">
          Bill.com payment of ${formatCurrency(
            pendingPayments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0)
          )} in flight
        </div>
      )}
    </div>
  ) : null;

  const renderEditableCell = (field, title, source, footnote = null) => (
    <td className="px-3 py-2 text-right tabular-nums text-sm">
      {editingField === field ? (
        <input
          autoFocus
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commit(field)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit(field);
            if (e.key === 'Escape') setEditingField(null);
          }}
          className="w-32 border border-gray-300 px-2 py-0.5 text-right text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-gray-700"
          style={{ background: OVERRIDE_BG }}
        />
      ) : (
        <span className="inline-flex flex-col items-end">
          <span className="inline-flex items-center gap-1">
            <span style={{ background: OVERRIDE_BG, padding: '1px 4px' }}>
              $ {formatCurrency(facility[field] ?? 0)}
            </span>
            {source && (
              <span className="text-[10px] text-gray-400" title={`${source} source`}>
                {source}
              </span>
            )}
            {onSave && facility.id != null && (
              <button
                type="button"
                onClick={() => startEdit(field)}
                className="text-gray-400 hover:text-gray-700"
                title={title}
              >
                <PencilIcon className="h-3 w-3" />
              </button>
            )}
          </span>
          {footnote}
        </span>
      )}
    </td>
  );

  const totalExposure = (facility.amountDue ?? 0) + (facility.accruedUnbilled ?? 0);

  return (
    <tr>
      <td className="px-3 py-2 text-sm font-medium">
        <div>{facility.vendorName}</div>
        {facility.billingSource && (
          <div className="text-[10px] font-normal text-gray-400">{facility.billingSource}</div>
        )}
      </td>
      {renderEditableCell('amountDue', 'Edit amount due', facility.amountDueSource, amountDueFootnote)}
      {renderEditableCell('accruedUnbilled', 'Edit accrued unbilled', facility.accruedUnbilledSource)}
      <td className="px-3 py-2 text-right tabular-nums text-sm font-medium">$ {formatCurrency(totalExposure)}</td>
      <td className="px-3 py-2 text-right text-sm text-gray-500">{facility.paymentDueDate ?? '—'}</td>
      <td className="px-3 py-2 text-right text-sm text-gray-500">{facility.paymentTerms ?? '—'}</td>
      <td className="px-3 py-2 text-right tabular-nums text-sm text-gray-500">
        {facility.creditLimit != null ? `$ ${formatCurrency(facility.creditLimit)}` : 'Unlimited'}
      </td>
    </tr>
  );
}

function EntityDetail({
  label,
  entity,
  lastSyncedAt,
  isKingsHolding,
  noAccountDetail,
  accountOverrides,
  onSaveAccountOverride,
  onSaveLimitOverride,
  onInvalidLimitOverride,
  onSaveManualReceivable,
  shopifyLiveBalance,
  shopifyBreakdown,
  amazonLiveBalance,
  amazonBreakdown,
  shopifyTotalOverride,
  shopifyTotalOverrideUpdatedAt,
  onSaveShopifyTotalOverride,
  amazonTotalOverride,
  amazonTotalOverrideUpdatedAt,
  onSaveAmazonTotalOverride,
  flexCreditOverride,
  onSaveFlexCreditOverride,
  operatingCredit,
  operatingCreditError,
  onSaveOperatingCredit,
}) {
  const ts = formatTimestamp(lastSyncedAt);
  const [acctEditingId, setAcctEditingId] = useState(null);
  const [acctDraft, setAcctDraft] = useState('');
  const [limitEditingId, setLimitEditingId] = useState(null);
  const [limitDraft, setLimitDraft] = useState('');
  const [flexEditing, setFlexEditing] = useState(false);
  const [flexDraft, setFlexDraft] = useState('');

  const hasOverride = (id) => accountOverrides != null && accountOverrides[id] != null;

  return (
    <div className="mb-8">
      <div className="entity-header mb-3 py-2 text-center text-sm">{label} — Effective Capital Summary</div>

      <div className="mb-4 overflow-x-auto">
        <div className="flex gap-px border border-gray-200" style={{ minWidth: 'max-content' }}>
          <div className="kpi-card" style={{ minWidth: 160 }}>
            <div className="kpi-card-label">{isKingsHolding ? 'Total Cash' : 'Cash & Receivables'}</div>
            <div className="kpi-card-value">
            <span>$</span> {formatCurrency((entity?.cash ?? 0) + (entity?.receivables ?? 0))}
            </div>
          </div>
          <div className="kpi-card" style={{ minWidth: 160 }}>
            <div className="kpi-card-label">CC Liabilities</div>
            <div className={`kpi-card-value ${(entity?.ccLiabilities ?? 0) < 0 ? 'value-negative' : ''}`}>
              <span>$</span> ({formatCurrency(Math.abs(entity?.ccLiabilities ?? 0))})
            </div>
          </div>
          <div className="kpi-card flex-1" style={{ minWidth: 160, background: EC_GREEN }}>
            <div className="kpi-card-label">Effective Capital</div>
            <div className={`kpi-card-value font-bold ${(entity?.effectiveCapital ?? 0) < 0 ? 'value-negative' : ''}`}><span>$</span> {formatSignedCurrency(entity?.effectiveCapital ?? 0)}</div>
          </div>
        </div>
      </div>

      {noAccountDetail && (
        <div className="mb-4 px-3 py-2 text-xs text-gray-500 border border-gray-200">
          Per-account detail is not available for this date — account-level snapshots were not captured for this period.
        </div>
      )}

      {entity?.bankAccounts?.length > 0 && (
        <div className="mb-4 overflow-x-auto">
          <table className="eadin-table">
            <thead>
              <tr>
                <th>Account Name</th>
                <th>Bank</th>
                <th>Source</th>
                <th className="!text-right">Timestamp</th>
                <th className="!text-right">Available Balance</th>
              </tr>
            </thead>
            <tbody>
              {entity.bankAccounts.map((acct) => {
                const override = hasOverride(acct.plaidAccountId);
                const displayBalance = override ? accountOverrides[acct.plaidAccountId] : acct.availableBalance;
                return (
                  <tr key={acct.plaidAccountId}>
                    <td className="px-3 py-2 text-sm">{acct.customName ?? acct.name}</td>
                    <td className="px-3 py-2 text-sm text-gray-500">{acct.subtype ?? acct.type}</td>
                    <td className="px-3 py-2 text-sm text-gray-500">
                      {override ? (
                        <span className="text-amber-600 font-medium">Manual override</span>
                      ) : (
                        'Plaid'
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-sm text-gray-500 tabular-nums">{ts}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-sm">
                      <EditableBalanceCell
                        plaidAccountId={acct.plaidAccountId}
                        displayBalance={displayBalance}
                        hasOverride={override}
                        editingId={acctEditingId}
                        setEditingId={setAcctEditingId}
                        onSave={onSaveAccountOverride}
                      />
                    </td>
                  </tr>
                );
              })}
              <tr className="subtotal-row">
                <td className="px-3 py-2 text-sm font-semibold" colSpan={3}>Total Cash</td>
                <td />
                <td className="px-3 py-2 text-right tabular-nums text-sm font-semibold">$ {formatCurrency(entity.cash)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {(entity?.receivableAccounts?.length > 0 || entity?.manualReceivables?.length > 0) && (
        <div className="mb-4 overflow-x-auto">
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1 mt-1">Accounts Receivable</div>
          <table className="eadin-table">
            <thead>
              <tr>
                <th>Account Name</th>
                <th>Processor</th>
                <th className="!text-right">Timestamp</th>
                <th className="!text-right">Available Balance</th>
              </tr>
            </thead>
            <tbody>
              {entity.receivableAccounts?.map((acct) => (
                <tr key={acct.plaidAccountId}>
                  <td className="px-3 py-2 text-sm">{acct.customName ?? acct.name}</td>
                  <td className="px-3 py-2 text-sm text-gray-500">Plaid</td>
                  <td className="px-3 py-2 text-right text-sm text-gray-500 tabular-nums">{ts}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-sm">$ {formatCurrency(acct.availableBalance)}</td>
                </tr>
              ))}
              {[...(entity.manualReceivables ?? [])]
                .sort((a, b) => {
                  const order = { shopify: 0, amazon: 1 };
                  const aOrder = order[a.source.toLowerCase()] ?? 99;
                  const bOrder = order[b.source.toLowerCase()] ?? 99;
                  if (aOrder !== bOrder) return aOrder - bOrder;
                  return a.label.localeCompare(b.label);
                })
                .map((mr) => {
                  if (mr.source.toLowerCase() === 'shopify' && shopifyLiveBalance !== undefined) {
                    return (
                      <ShopifyReceivableRow
                        key={mr.source}
                        row={mr}
                        liveBalance={shopifyLiveBalance}
                        breakdown={shopifyBreakdown}
                        totalOverride={shopifyTotalOverride}
                        totalOverrideUpdatedAt={shopifyTotalOverrideUpdatedAt}
                        onSaveTotalOverride={onSaveShopifyTotalOverride}
                      />
                    );
                  }
                  if (mr.source.toLowerCase() === 'amazon' && amazonLiveBalance !== undefined) {
                    return (
                      <AmazonReceivableRow
                        key={mr.source}
                        row={mr}
                        liveBalance={amazonLiveBalance}
                        breakdown={amazonBreakdown}
                        totalOverride={amazonTotalOverride}
                        totalOverrideUpdatedAt={amazonTotalOverrideUpdatedAt}
                        onSaveTotalOverride={onSaveAmazonTotalOverride}
                      />
                    );
                  }
                  return (
                    <ManualReceivableEditRow
                      key={mr.source}
                      row={mr}
                      onSave={onSaveManualReceivable ?? (() => {})}
                    />
                  );
                })}
              <tr className="subtotal-row">
                <td className="px-3 py-2 text-sm font-semibold" colSpan={3}>Total Receivables</td>
                <td className="px-3 py-2 text-right tabular-nums text-sm font-semibold">$ {formatCurrency(entity.receivables)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {!isKingsHolding && operatingCreditError && (
        <div className="mb-4 border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          Operating Credit failed to load: {operatingCreditError}
        </div>
      )}

      {!isKingsHolding && !operatingCreditError && operatingCredit?.facilities?.length > 0 && (
        <div className="mb-4 overflow-x-auto">
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1 mt-1">Operating Credit</div>
          <table className="eadin-table">
            <thead>
              <tr>
                <th>Vendor</th>
                <th className="!text-right">Amount Due</th>
                <th className="!text-right">Accrued (Unbilled)</th>
                <th className="!text-right">Total Exposure</th>
                <th className="!text-right">Payment Due</th>
                <th className="!text-right">Terms</th>
                <th className="!text-right">Credit Limit</th>
              </tr>
            </thead>
            <tbody>
              {operatingCredit.facilities.map((facility) => (
                <OperatingCreditRow key={facility.id} facility={facility} onSave={onSaveOperatingCredit} />
              ))}
              <tr className="subtotal-row">
                <td className="px-3 py-2 text-sm font-semibold">Total Operating Credit</td>
                <td className="px-3 py-2 text-right tabular-nums text-sm font-semibold">$ {formatCurrency(operatingCredit.totalAmountDue)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-sm font-semibold">$ {formatCurrency(operatingCredit.totalAccruedUnbilled)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-sm font-semibold">$ {formatCurrency(operatingCredit.totalExposure)}</td>
                <td />
                <td />
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {entity?.creditCards?.length > 0 && (
        <div className="mb-4 overflow-x-auto">
          <table className="eadin-table">
            <thead>
              <tr>
                <th>Credit Card</th>
                <th>CC #</th>
                <th className="!text-right">Payment Due</th>
                <th className="!text-right">Limit</th>
                <th className="!text-right">Remaining Limit</th>
                <th>Source</th>
                <th className="!text-right">Timestamp</th>
                <th className="!text-right">Total Balance</th>
              </tr>
            </thead>
            <tbody>
              {[...entity.creditCards]
                .sort((a, b) => (a.paymentDueDay ?? 999) - (b.paymentDueDay ?? 999))
                .map((card) => {
                  const limit = card.creditLimit ?? card.balanceLimit ?? 0;
                  const posted = postedBalance(card);
                  const remaining = limit - posted;
                  const isFlex = (card.customName ?? card.name).toLowerCase().includes('flex');
                  const perAcctOverride = accountOverrides != null ? accountOverrides[card.plaidAccountId] : null;
                  const hasPerAcctOverride = perAcctOverride != null;
                  const hasFlexOverride = isFlex && flexCreditOverride != null && !hasPerAcctOverride;
                  const hasAnyOverride = hasPerAcctOverride || hasFlexOverride;
                  const hasLimitOverride = card.limitOverride != null;
                  const displayBalance = hasPerAcctOverride
                    ? perAcctOverride
                    : (isFlex && flexCreditOverride != null ? flexCreditOverride : posted);
                  const displayRemaining = limit > 0 ? limit - displayBalance : remaining;

                  return (
                    <tr
                      key={card.plaidAccountId}
                      style={isFlex ? { borderLeft: FLEX_BORDER, fontWeight: 600 } : {}}
                    >
                      <td className="px-3 py-2 text-sm font-medium">{card.customName ?? card.name}</td>
                      <td className="px-3 py-2 text-sm text-gray-500 tabular-nums">{card.mask ? `••${card.mask}` : '—'}</td>
                      <td className="px-3 py-2 text-right text-sm">{formatPaymentDue(card)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-sm">
                        {onSaveLimitOverride ? (
                          limitEditingId === card.plaidAccountId ? (
                            <span className="inline-flex items-center gap-1.5 justify-end">
                              <input
                                autoFocus
                                type="text"
                                value={limitDraft}
                                onChange={(e) => setLimitDraft(e.target.value)}
                                onBlur={() => {
                                  const val = parseFloat(limitDraft.replace(/[$,]/g, ''));
                                  if (!Number.isNaN(val) && val >= 0) onSaveLimitOverride(card.plaidAccountId, val);
                                  else if (limitDraft.trim() === '') onSaveLimitOverride(card.plaidAccountId, null);
                                  else onInvalidLimitOverride();
                                  setLimitEditingId(null);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const val = parseFloat(limitDraft.replace(/[$,]/g, ''));
                                    if (!Number.isNaN(val) && val >= 0) onSaveLimitOverride(card.plaidAccountId, val);
                                    else if (limitDraft.trim() === '') onSaveLimitOverride(card.plaidAccountId, null);
                                    else onInvalidLimitOverride();
                                    setLimitEditingId(null);
                                  }
                                  if (e.key === 'Escape') setLimitEditingId(null);
                                }}
                                className="w-28 border border-gray-300 px-1.5 py-0.5 text-right text-xs tabular-nums focus:outline-none focus:ring-1 focus:ring-gray-900"
                                style={{ background: OVERRIDE_BG }}
                              />
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 justify-end">
                              <span style={hasLimitOverride ? { background: OVERRIDE_BG, padding: '1px 4px' } : {}}>
                                {limit > 0 ? <><span>$</span>{formatCurrency(limit)}</> : '—'}
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  setAcctEditingId(null);
                                  setLimitDraft(limit > 0 ? String(limit) : '');
                                  setLimitEditingId(card.plaidAccountId);
                                }}
                                className="inline-flex items-center text-gray-500 hover:text-gray-900 transition-colors"
                                title={hasLimitOverride ? 'Edit limit override' : 'Override credit limit'}
                              ><PencilIcon className="w-3 h-3 shrink-0" /></button>
                              {hasLimitOverride && (
                                <button
                                  type="button"
                                  onClick={() => onSaveLimitOverride(card.plaidAccountId, null)}
                                  className="inline-flex items-center text-gray-500 hover:text-gray-900 transition-colors"
                                  title="Clear limit override"
                                ><XMarkIcon className="w-3 h-3 shrink-0" /></button>
                              )}
                            </span>
                          )
                        ) : (
                          limit > 0 ? <div><span>$</span>{formatCurrency(limit)}</div> : '—'
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-sm">
                        {limit > 0 ? <div><span>$</span>{formatCurrency(displayRemaining)}</div> : '—'}
                      </td>
                      <td className="px-3 py-2 text-sm">
                        {hasAnyOverride ? (
                          <span className="text-xs font-medium text-blue-700">Manual override</span>
                        ) : (
                          <span className="text-xs text-gray-500">Plaid</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-sm text-gray-500 tabular-nums">{ts}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-sm value-negative">
                        {onSaveAccountOverride ? (
                          <span className="inline-flex items-center gap-1.5 justify-end">
                            {acctEditingId === card.plaidAccountId ? (
                              <input
                                autoFocus
                                type="text"
                                value={acctDraft}
                                onChange={(e) => setAcctDraft(e.target.value)}
                                onBlur={() => {
                                  const val = parseFloat(acctDraft.replace(/[$,]/g, ''));
                                  if (!Number.isNaN(val)) onSaveAccountOverride(card.plaidAccountId, val);
                                  else if (acctDraft.trim() === '') onSaveAccountOverride(card.plaidAccountId, null);
                                  setAcctEditingId(null);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const val = parseFloat(acctDraft.replace(/[$,]/g, ''));
                                    if (!Number.isNaN(val)) onSaveAccountOverride(card.plaidAccountId, val);
                                    else if (acctDraft.trim() === '') onSaveAccountOverride(card.plaidAccountId, null);
                                    setAcctEditingId(null);
                                  }
                                  if (e.key === 'Escape') setAcctEditingId(null);
                                }}
                                className="w-28 border border-gray-300 px-1.5 py-0.5 text-right text-xs tabular-nums focus:outline-none focus:ring-1 focus:ring-gray-900"
                                style={{ background: OVERRIDE_BG }}
                              />
                            ) : (
                              <>
                                <span style={hasAnyOverride ? { background: OVERRIDE_BG, padding: '1px 4px' } : {}}>({formatCurrency(displayBalance)})</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setLimitEditingId(null);
                                    setAcctDraft(String(displayBalance));
                                    setAcctEditingId(card.plaidAccountId);
                                  }}
                                  className="inline-flex items-center text-gray-500 hover:text-gray-900 transition-colors"
                                  title={hasAnyOverride ? 'Edit balance override' : 'Override balance'}
                                ><PencilIcon className="w-3 h-3 shrink-0" /></button>
                                {hasAnyOverride && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (hasPerAcctOverride) onSaveAccountOverride(card.plaidAccountId, null);
                                      else if (hasFlexOverride && onSaveFlexCreditOverride) onSaveFlexCreditOverride(null);
                                    }}
                                    className="inline-flex items-center text-gray-500 hover:text-gray-900 transition-colors"
                                    title="Clear override"
                                  ><XMarkIcon className="w-3 h-3 shrink-0" /></button>
                                )}
                              </>
                            )}
                          </span>
                        ) : isFlex && onSaveFlexCreditOverride ? (
                          <span className="inline-flex items-center gap-1.5 justify-end">
                            {flexEditing ? (
                              <input
                                autoFocus
                                type="text"
                                value={flexDraft}
                                onChange={(e) => setFlexDraft(e.target.value)}
                                onBlur={() => {
                                  const val = parseFloat(flexDraft.replace(/[$,]/g, ''));
                                  if (!Number.isNaN(val)) onSaveFlexCreditOverride(val);
                                  else if (flexDraft.trim() === '') onSaveFlexCreditOverride(null);
                                  setFlexEditing(false);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const val = parseFloat(flexDraft.replace(/[$,]/g, ''));
                                    if (!Number.isNaN(val)) onSaveFlexCreditOverride(val);
                                    else if (flexDraft.trim() === '') onSaveFlexCreditOverride(null);
                                    setFlexEditing(false);
                                  }
                                  if (e.key === 'Escape') setFlexEditing(false);
                                }}
                                className="w-28 border border-gray-300 px-1.5 py-0.5 text-right text-xs tabular-nums focus:outline-none focus:ring-1 focus:ring-gray-900"
                                style={{ background: OVERRIDE_BG }}
                              />
                            ) : (
                              <>
                                <span style={hasFlexOverride ? { background: OVERRIDE_BG, padding: '1px 4px' } : {}}>({formatCurrency(displayBalance)})</span>
                                <button
                                  type="button"
                                  onClick={() => { setFlexDraft(String(displayBalance)); setFlexEditing(true); }}
                                  className="inline-flex items-center text-gray-500 hover:text-gray-900 transition-colors"
                                  title={hasFlexOverride ? 'Edit balance override' : 'Override balance'}
                                ><PencilIcon className="w-3 h-3 shrink-0" /></button>
                                {hasFlexOverride && (
                                  <button
                                    type="button"
                                    onClick={() => onSaveFlexCreditOverride(null)}
                                    className="inline-flex items-center text-gray-500 hover:text-gray-900 transition-colors"
                                    title="Clear override"
                                  ><XMarkIcon className="w-3 h-3 shrink-0" /></button>
                                )}
                              </>
                            )}
                          </span>
                        ) : (
                          <span>({formatCurrency(displayBalance)})</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              <tr className="subtotal-row">
                <td className="px-3 py-2 text-sm font-semibold">Total CC Liabilities</td>
                <td />
                <td />
                <td className="px-3 py-2 text-right tabular-nums text-sm font-semibold">
                  ${formatCurrency(entity.creditCards.reduce((s, c) => s + (c.creditLimit ?? c.balanceLimit ?? 0), 0))}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-sm font-semibold">
                  ${formatCurrency(entity.creditCards.reduce((s, c) => {
                    const lim = c.creditLimit ?? c.balanceLimit ?? 0;
                    const posted = postedBalance(c);
                    return s + (lim - posted);
                  }, 0))}
                </td>
                <td />
                <td />
                <td className="px-3 py-2 text-right tabular-nums text-sm font-semibold value-negative">
                  ({formatCurrency(Math.abs(entity.ccLiabilities))})
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function entityHasAccounts(entity) {
  if (!entity) return false;
  return (
    (entity.bankAccounts?.length ?? 0) > 0 ||
    (entity.receivableAccounts?.length ?? 0) > 0 ||
    (entity.creditCards?.length ?? 0) > 0 ||
    (entity.locAccounts?.length ?? 0) > 0
  );
}

export const EffectiveCapitalSummaryCapitalSummary = ({ onNavigateTab, onLoadingChange, viewSwitcher, onExportPdf, pdfLoading }) => {
  const today = todayISO();
  const yesterday = yesterdayISO();
  const { currentUser } = useSelector((state) => state.auth);
  const cacheUserId = currentUser?.id ?? 'anon';

  const [selectedDate, setSelectedDate] = useState(null);
  const isHistorical = selectedDate !== null && selectedDate !== today;

  // Live data
  const [liveSummary, setLiveSummary] = useState(() => readEffectiveCapitalCache(cacheUserId));
  const [summaryLoading, setSummaryLoading] = useState(() => liveSummary === null);
  const [summaryLoaded, setSummaryLoaded] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [dodDeltas, setDodDeltas] = useState([]);
  const [shopifyBalance, setShopifyBalance] = useState(null);
  const [amazonBalance, setAmazonBalance] = useState(null);
  const [shopifyTotalOverrideData, setShopifyTotalOverrideData] = useState(null);
  const [amazonTotalOverrideData, setAmazonTotalOverrideData] = useState(null);
  const [flexCreditOverrideData, setFlexCreditOverrideData] = useState(null);

  // Historical data
  const [historicalRows, setHistoricalRows] = useState([]);
  const [historicalAccountRows, setHistoricalAccountRows] = useState([]);
  const [historicalReceivablesExact, setHistoricalReceivablesExact] = useState([]);
  const [historicalReceivablesLatest, setHistoricalReceivablesLatest] = useState([]);
  const [historicalOperatingCredit, setHistoricalOperatingCredit] = useState([]);
  const [historicalOperatingCreditError, setHistoricalOperatingCreditError] = useState(null);
  const [histLoading, setHistLoading] = useState(false);

  const loadLiveSummary = useCallback(async () => {
    try {
      const data = await effectiveCapitalApi.getSummary();
      setLiveSummary(data);
      writeEffectiveCapitalCache(cacheUserId, data);
      setSummaryError(null);
      setSummaryLoaded(true);
    } catch (error) {
      // toast.error(error?.response?.data?.error || 'Failed to load capital summary', { position: 'bottom-right' });
      setSummaryError(error);
      setSummaryLoaded(true);
    }
  }, [cacheUserId]);

  const loadLiveExtras = useCallback(async () => {
    const [dod, shopify, amazon, shopifyTotal, amazonTotal, flexCredit] = await Promise.allSettled([
      effectiveCapitalApi.getDoDelta(today, yesterday),
      effectiveCapitalApi.getShopifyBalance(),
      effectiveCapitalApi.getAmazonBalance(),
      effectiveCapitalApi.getShopifyTotalOverride(),
      effectiveCapitalApi.getAmazonTotalOverride(),
      effectiveCapitalApi.getFlexCreditOverride(),
    ]);
    setDodDeltas(dod.status === 'fulfilled' ? dod.value ?? [] : []);
    setShopifyBalance(shopify.status === 'fulfilled' ? shopify.value : null);
    setAmazonBalance(amazon.status === 'fulfilled' ? amazon.value : null);
    setShopifyTotalOverrideData(shopifyTotal.status === 'fulfilled' ? shopifyTotal.value : null);
    setAmazonTotalOverrideData(amazonTotal.status === 'fulfilled' ? amazonTotal.value : null);
    setFlexCreditOverrideData(flexCredit.status === 'fulfilled' ? flexCredit.value : null);
  }, [today, yesterday]);

  const loadLiveData = useCallback(async () => {
    await Promise.all([loadLiveSummary(), loadLiveExtras()]);
  }, [loadLiveSummary, loadLiveExtras]);

  const loadHistoricalData = useCallback(async (date) => {
    if (!date) return;
    const [rows, acctRows, exact, latest, operatingCredit] = await Promise.allSettled([
      effectiveCapitalApi.getEcSnapshotForDate(date),
      effectiveCapitalApi.getAccountDetailForDate(date),
      effectiveCapitalApi.getReceivablesForDate(date),
      effectiveCapitalApi.getReceivablesLatestBefore(date),
      effectiveCapitalApi.getOperatingCreditForDate(date),
    ]);
    const failedEndpoints = [
      ['Effective Capital snapshot', rows],
      ['account detail', acctRows],
      ['receivables', exact],
      ['receivables history', latest],
      ['operating credit', operatingCredit],
    ].filter(([, result]) => result.status === 'rejected');
    if (failedEndpoints.length > 0) {
      const errorMessage = failedEndpoints
        .map(([label, result]) => {
          const detail = result.reason?.response?.data?.error || result.reason?.message;
          return `${label}: ${detail || 'request failed'}`;
        })
        .join('; ');
      toast.error(`Failed to load historical data — ${errorMessage}`, { position: 'bottom-right' });
    }
    setHistoricalRows(rows.status === 'fulfilled' ? rows.value ?? [] : []);
    setHistoricalAccountRows(acctRows.status === 'fulfilled' ? acctRows.value ?? [] : []);
    setHistoricalReceivablesExact(exact.status === 'fulfilled' ? exact.value ?? [] : []);
    setHistoricalReceivablesLatest(latest.status === 'fulfilled' ? latest.value ?? [] : []);
    setHistoricalOperatingCredit(operatingCredit.status === 'fulfilled' ? operatingCredit.value ?? [] : []);
    setHistoricalOperatingCreditError(
      operatingCredit.status === 'rejected'
        ? (operatingCredit.reason?.response?.data?.error || operatingCredit.reason?.message || 'request failed')
        : null
    );
  }, []);

  // Always keep the live summary fresh (canonical list of manual receivable sources),
  // polling only while in live mode.
  useEffect(() => {
    if (isHistorical) return;
    loadLiveData().finally(() => setSummaryLoading(false));
    const interval = setInterval(loadLiveData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isHistorical, loadLiveData]);

  useEffect(() => {
    if (isHistorical) {
      setDodDeltas([]);
    }
  }, [isHistorical]);

  useEffect(() => {
    if (!isHistorical || !selectedDate) return;
    setHistLoading(true);
    loadHistoricalData(selectedDate)
      .finally(() => setHistLoading(false));
  }, [isHistorical, selectedDate, loadHistoricalData]);

  const liveReceivableSources = liveSummary?.kingsLoot?.manualReceivables ?? [];

  const historicalReceivables = useMemo(() => {
    if (!isHistorical) return [];
    const exactMap = new Map(historicalReceivablesExact.map((r) => [r.source, r]));
    const latestMap = new Map(historicalReceivablesLatest.map((r) => [r.source, r]));
    const allSources = new Map();
    historicalReceivablesLatest.forEach((r) => allSources.set(r.source, r));
    historicalReceivablesExact.forEach((r) => allSources.set(r.source, r));
    liveReceivableSources.forEach((r) => {
      if (!allSources.has(r.source)) allSources.set(r.source, { ...r, amount: 0 });
    });
    return Array.from(allSources.values()).map((base) => {
      const exact = exactMap.get(base.source);
      const latest = latestMap.get(base.source);
      return exact ?? latest ?? { ...base, amount: 0 };
    });
  }, [isHistorical, historicalReceivablesExact, historicalReceivablesLatest, liveReceivableSources]);

  const historicalSummary = useMemo(() => {
    if (!isHistorical || historicalRows.length === 0) return null;
    const kl = historicalRows.find((r) => r.entity === 'kings_loot');
    const kh = historicalRows.find((r) => r.entity === 'kings_holding');
    const con = historicalRows.find((r) => r.entity === 'consolidated');
    if (!kl || !kh || !con) return null;

    const normalizedAccounts = historicalAccountRows.map((a) => ({
      ...a,
      availableBalance: toNum(a.availableBalance),
      currentBalance: toNum(a.currentBalance),
      balanceLimit: a.balanceLimit != null ? toNum(a.balanceLimit) : null,
      creditLimit: a.balanceLimit != null ? toNum(a.balanceLimit) : null,
    }));

    const nameIncludesAcct = (a, s) =>
      (a.customName ?? a.name ?? '').toLowerCase().includes(s) ||
      (a.officialName ?? '').toLowerCase().includes(s);

    const acctsByEntity = (entity) => normalizedAccounts.filter((a) => a.entity === entity);

    const classifyEntity = (entity) => {
      const accts = acctsByEntity(entity);
      const bankAccounts = accts.filter((a) => a.type === 'depository');
      const receivableAccounts = accts.filter(
        (a) => a.type !== 'credit' && (nameIncludesAcct(a, 'receivable') || nameIncludesAcct(a, 'shopify') || nameIncludesAcct(a, 'amazon'))
      );
      const creditCards = accts.filter((a) => a.type === 'credit');
      const ownLocAccounts = accts.filter((a) => a.type === 'loan' || a.subtype === 'line of credit');
      const khLoanAccounts = entity === 'kings_loot'
        ? normalizedAccounts.filter((a) => a.entity === 'kings_holding' && (a.type === 'loan' || a.subtype === 'line of credit'))
        : [];
      const locAccounts = [...ownLocAccounts, ...khLoanAccounts];
      return { bankAccounts, receivableAccounts, creditCards, locAccounts };
    };

    const makeEntity = (row, entityLabel, entity) => ({
      entity,
      entityLabel,
      cash: toNum(row.cash),
      receivables: toNum(row.receivables),
      manualReceivables: entity === 'kings_loot' ? historicalReceivables : [],
      locNetAvailability: toNum(row.locNetAvailability),
      locBorrowingCapacity: toNum(row.locBorrowingCapacity),
      locOutstanding: toNum(row.locOutstanding),
      ccLiabilities: toNum(row.ccLiabilities),
      ocLiabilities: toNum(row.ocLiabilities),
      effectiveCapital: toNum(row.effectiveCapital),
      operatingCredit: entity === 'kings_loot' ? {
        facilities: historicalOperatingCredit,
        totalAmountDue: historicalOperatingCredit.reduce((sum, facility) => sum + toNum(facility.amountDue), 0),
        totalAccruedUnbilled: historicalOperatingCredit.reduce((sum, facility) => sum + toNum(facility.accruedUnbilled), 0),
        totalExposure: historicalOperatingCredit.reduce(
          (sum, facility) => sum + toNum(facility.amountDue) + toNum(facility.accruedUnbilled),
          0
        ),
      } : null,
      ...classifyEntity(entity),
    });

    return {
      asOf: selectedDate,
      lastSyncedAt: kl.capturedAt,
      kingsLoot: makeEntity(kl, 'Kings Loot', 'kings_loot'),
      kingsHolding: makeEntity(kh, 'Kings Holding', 'kings_holding'),
      khCashReserve: toNum(kh.cash),
      consolidated: {
        cash: toNum(con.cash),
        receivables: toNum(con.receivables),
        locNetAvailability: toNum(con.locNetAvailability),
        locBorrowingCapacity: toNum(con.locBorrowingCapacity),
        locOutstanding: toNum(con.locOutstanding),
        ccLiabilities: toNum(con.ccLiabilities),
        ocLiabilities: toNum(con.ocLiabilities),
        effectiveCapital: toNum(con.effectiveCapital),
      },
    };
  }, [isHistorical, historicalRows, historicalAccountRows, historicalReceivables, historicalOperatingCredit, selectedDate]);

  const summary = isHistorical ? historicalSummary : liveSummary;
  const isLoading = isHistorical ? histLoading : summaryLoading;

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  const dateLabel = isHistorical && selectedDate
    ? formatDateLong(new Date(`${selectedDate}T12:00:00`))
    : formatDateLong(new Date());

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const result = await effectiveCapitalApi.syncBalances();
      const errs = result?.errors?.length ?? 0;
      if (errs > 0) {
        toast.warning(`Sync complete: ${result.synced} accounts synced, ${errs} error(s)`, { position: 'bottom-right' });
      } else {
        toast.success(`Sync complete: ${result.synced} accounts synced`, { position: 'bottom-right' });
      }
      await loadLiveData();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Sync failed', { position: 'bottom-right' });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleRetry = async () => {
    setSummaryLoading(true);
    await loadLiveData();
    setSummaryLoading(false);
  };

  const handleSaveAccountOverride = async (plaidAccountId, amount) => {
    try {
      await effectiveCapitalApi.setBalanceOverride(plaidAccountId, amount);
      await loadLiveData();
      toast.success(amount == null ? 'Override cleared' : 'Balance override saved', { position: 'bottom-right' });
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to save override', { position: 'bottom-right' });
    }
  };

  const handleSaveLimitOverride = async (plaidAccountId, amount) => {
    try {
      await effectiveCapitalApi.setLimitOverride(plaidAccountId, amount);
      await loadLiveData();
      toast.success(amount == null ? 'Limit override cleared' : 'Limit override saved', { position: 'bottom-right' });
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to save limit override', { position: 'bottom-right' });
    }
  };

  const handleInvalidLimitOverride = () => {
    toast.error('Limit must be a non-negative number', { position: 'bottom-right' });
  };

  const handleSaveManualReceivable = async (source, label, amount) => {
    try {
      await effectiveCapitalApi.upsertManualReceivable({
        source,
        label,
        amount,
        date: isHistorical ? selectedDate : today,
      });
      if (isHistorical && selectedDate) {
        await loadHistoricalData(selectedDate);
      } else {
        await loadLiveSummary();
      }
      toast.success('Receivable updated', { position: 'bottom-right' });
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to save receivable', { position: 'bottom-right' });
    }
  };

  const handleSaveOperatingCredit = async (id, field, value) => {
    try {
      await effectiveCapitalApi.updateOperatingCredit(id, {
        [field]: value,
        ...(isHistorical && selectedDate ? { date: selectedDate } : {}),
      });
      if (isHistorical && selectedDate) {
        await loadHistoricalData(selectedDate);
      } else {
        await loadLiveSummary();
      }
      toast.success('Operating credit updated', { position: 'bottom-right' });
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to update operating credit', { position: 'bottom-right' });
    }
  };

  const handleSaveShopifyTotalOverride = async (amount) => {
    try {
      await effectiveCapitalApi.setShopifyTotalOverride(amount);
      setShopifyTotalOverrideData(await effectiveCapitalApi.getShopifyTotalOverride());
      await loadLiveSummary();
      toast.success(amount == null ? 'Shopify override cleared' : 'Shopify total override saved', { position: 'bottom-right' });
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to save Shopify total override', { position: 'bottom-right' });
    }
  };

  const handleSaveAmazonTotalOverride = async (amount) => {
    try {
      await effectiveCapitalApi.setAmazonTotalOverride(amount);
      setAmazonTotalOverrideData(await effectiveCapitalApi.getAmazonTotalOverride());
      await loadLiveSummary();
      toast.success(amount == null ? 'Amazon override cleared' : 'Amazon total override saved', { position: 'bottom-right' });
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to save Amazon total override', { position: 'bottom-right' });
    }
  };

  const handleSaveFlexCreditOverride = async (amount) => {
    try {
      await effectiveCapitalApi.setFlexCreditOverride(amount);
      setFlexCreditOverrideData(await effectiveCapitalApi.getFlexCreditOverride());
      await loadLiveSummary();
      toast.success(amount == null ? 'Flex Credit override cleared' : 'Flex Credit override saved', { position: 'bottom-right' });
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to save Flex Credit override', { position: 'bottom-right' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center text-sm text-gray-500 mt-10">
        <LoaderSpinner height="80" width="80" colorSpinner="black" border="4" />
      </div>
    );
  }

  const hasAccounts = entityHasAccounts(summary?.kingsLoot) || entityHasAccounts(summary?.kingsHolding);

  return (
    <div className="flex flex-col">
      <div className="shrink-0 py-3 border-b border-gray-200 mb-4">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            {viewSwitcher ?? (
              <h2 className="text-sm font-bold uppercase tracking-widest">Effective Capital Summary</h2>
            )}
          </div>
          {!isHistorical && summary?.lastSyncedAt && (
            <p className="text-[11px] text-gray-500 shrink-0">Last sync: {formatSyncTimestamp(summary.lastSyncedAt)}</p>
          )}
          {isHistorical && summary?.lastSyncedAt && (
            <p className="text-[11px] text-gray-500 shrink-0">Snapshot captured: {formatSyncTimestamp(summary.lastSyncedAt)}</p>
          )}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-1.5 md:justify-end">
          <div className="flex items-center gap-1">
            <div className="w-36">
              <DatePickerInput
                value={selectedDate ?? today}
                onChange={(v) => setSelectedDate(!v || v === today ? null : v)}
                max={today}
              />
            </div>
            {isHistorical && (
              <button
                type="button"
                onClick={() => setSelectedDate(null)}
                className="text-gray-500 hover:text-gray-900 shrink-0"
                title="Return to live view"
              >
                <XMarkIcon className="w-3.5 h-3.5 shrink-0" />
              </button>
            )}
          </div>
          {!isHistorical && (
            <button type="button" onClick={handleSync} disabled={isSyncing} className="flex items-center gap-1.5 border border-gray-300 rounded-md px-2.5 py-1.5 text-xs font-medium hover:bg-gray-50 disabled:opacity-50">
              <ArrowPathIcon className={`w-3.5 h-3.5 shrink-0 ${isSyncing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">{isSyncing ? 'Syncing...' : 'Sync Balances'}</span>
            </button>
          )}
          {onExportPdf && (
            <button
              type="button"
              onClick={() => onExportPdf(selectedDate ?? today)}
              disabled={pdfLoading}
              className="flex items-center gap-1.5 bg-gray-900 rounded-md px-2.5 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:inline">{pdfLoading ? 'Generating...' : 'Export PDF'}</span>
            </button>
          )}
        </div>
      </div>

      {isHistorical && (
        <div className="flex items-center justify-between px-4 py-2 mb-4 text-xs bg-blue-50 border border-blue-100 rounded-lg">
          <span className="font-medium text-blue-700">Historical View — {dateLabel}</span>
          <button type="button" onClick={() => setSelectedDate(null)} className="flex items-center gap-1 text-blue-700 hover:underline">
            <XMarkIcon className="w-3 h-3 shrink-0" /> Return to live view
          </button>
        </div>
      )}


      {isHistorical && !summary ? (
        <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
          <div className="text-sm font-medium">No snapshot available for {dateLabel}</div>
          <p className="max-w-sm text-xs text-gray-500">
            Snapshots are saved automatically after each daily sync. Data will be available starting from today.
          </p>
          <button
            type="button"
            onClick={() => setSelectedDate(null)}
            className="inline-flex items-center gap-1.5 border border-gray-300 px-4 py-2 text-xs font-medium hover:bg-gray-50"
          >
            Return to live view
          </button>
        </div>
      ) : !isHistorical && summaryError && !summary ? (
        <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
          <div className="text-sm font-medium">Couldn't load the Effective Capital Summary</div>
          <button
            type="button"
            onClick={handleRetry}
            className="inline-flex items-center gap-1.5 border border-gray-300 px-4 py-2 text-xs font-medium hover:bg-gray-50"
          >
            Retry
          </button>
        </div>
      ) : !isHistorical && summaryLoaded && !hasAccounts ? (
        <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
          <div className="text-sm font-medium">No accounts connected yet</div>
          <p className="max-w-sm text-xs text-gray-500">
            Connect your bank accounts via Plaid to see your live Effective Capital Summary.
          </p>
          <button
            type="button"
            onClick={() => onNavigateTab?.('connect-accounts')}
            className="inline-flex items-center gap-1.5 bg-gray-900 px-4 py-2 text-xs font-medium text-white hover:opacity-90"
          >
            Connect Accounts
          </button>
        </div>
      ) : (
        <>
          <ConsolidatedTable summary={summary} dodDeltas={dodDeltas} date={dateLabel} />
          <EntityDetail
            label="Kings Loot"
            entity={summary.kingsLoot}
            lastSyncedAt={summary.lastSyncedAt}
            noAccountDetail={isHistorical && historicalAccountRows.length === 0}
            onSaveManualReceivable={handleSaveManualReceivable}
            khCashReserve={summary.khCashReserve ?? 0}
            shopifyLiveBalance={isHistorical ? undefined : shopifyBalance?.amount}
            shopifyBreakdown={isHistorical ? undefined : shopifyBalance?.breakdown}
            amazonLiveBalance={isHistorical ? undefined : amazonBalance?.amount}
            amazonBreakdown={isHistorical ? undefined : amazonBalance?.breakdown}
            shopifyTotalOverride={isHistorical ? undefined : shopifyTotalOverrideData?.amount}
            shopifyTotalOverrideUpdatedAt={isHistorical ? undefined : shopifyTotalOverrideData?.updatedAt}
            onSaveShopifyTotalOverride={isHistorical ? undefined : handleSaveShopifyTotalOverride}
            amazonTotalOverride={isHistorical ? undefined : amazonTotalOverrideData?.amount}
            amazonTotalOverrideUpdatedAt={isHistorical ? undefined : amazonTotalOverrideData?.updatedAt}
            onSaveAmazonTotalOverride={isHistorical ? undefined : handleSaveAmazonTotalOverride}
            flexCreditOverride={isHistorical ? undefined : flexCreditOverrideData?.amount}
            onSaveFlexCreditOverride={isHistorical ? undefined : handleSaveFlexCreditOverride}
            accountOverrides={isHistorical ? undefined : liveSummary?.accountOverrides}
            onSaveAccountOverride={isHistorical ? undefined : handleSaveAccountOverride}
            onSaveLimitOverride={isHistorical ? undefined : handleSaveLimitOverride}
            onInvalidLimitOverride={isHistorical ? undefined : handleInvalidLimitOverride}
            operatingCredit={isHistorical ? historicalSummary?.kingsLoot?.operatingCredit : liveSummary?.kingsLoot?.operatingCredit}
            operatingCreditError={isHistorical ? historicalOperatingCreditError : null}
            onSaveOperatingCredit={handleSaveOperatingCredit}
          />
          <EntityDetail
            label="Kings Holding"
            entity={summary.kingsHolding}
            lastSyncedAt={summary.lastSyncedAt}
            noAccountDetail={isHistorical && historicalAccountRows.length === 0}
            isKingsHolding
            accountOverrides={isHistorical ? undefined : liveSummary?.accountOverrides}
            onSaveAccountOverride={isHistorical ? undefined : handleSaveAccountOverride}
            onSaveLimitOverride={isHistorical ? undefined : handleSaveLimitOverride}
            onInvalidLimitOverride={isHistorical ? undefined : handleInvalidLimitOverride}
          />
        </>
      )}
    </div>
  );
};
